import {
    createArtifactShareLink,
    downloadArtifact,
} from '@api/reportingAgent';
import type { DownloadArtifactPayload } from '@diagon-alley/common';
import {
    DownloadSimple,
    EnvelopeSimple,
} from '@phosphor-icons/react';
import { extractApiErrorMessage } from '@utils/api-error';
import { useEffect, useState } from 'react';

import styles from './ReportsContainer.module.css';

interface ReportActionBarProps {
    payload: DownloadArtifactPayload;
}

interface PreparedShareLinks {
    csvUrl: string;
    csvExpiresAt: string;
    xlsxFilename?: string;
    xlsxUrl?: string;
}

/**
 * Slim text-link action bar that sits flush under a report's result
 * card. Replaces the old DownloadCard which had its own card chrome —
 * the visual spec calls for actions to read as inline links, not as
 * a competing card. Share-links are pre-fetched so the Email click
 * handler can stay synchronous (Chrome cancels protocol dispatch when
 * the user gesture is consumed by an await; the Gmail compose URL
 * trick we use sidesteps that, but still demands a sync handler).
 */
export default function ReportActionBar({ payload }: ReportActionBarProps) {
    const { url, filename, format, alternates } = payload;
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [shareLinks, setShareLinks] = useState<PreparedShareLinks | null>(
        null,
    );

    useEffect(() => {
        let cancelled = false;
        const xlsxAlt = alternates?.find((a) => a.format === 'xlsx');
        (async () => {
            try {
                const csvLink = await createArtifactShareLink(url);
                const xlsxLink = xlsxAlt
                    ? await createArtifactShareLink(xlsxAlt.url)
                    : null;
                if (cancelled) return;
                setShareLinks({
                    csvUrl: csvLink.url,
                    csvExpiresAt: csvLink.expiresAt,
                    xlsxFilename: xlsxAlt?.filename,
                    xlsxUrl: xlsxLink?.url,
                });
            } catch {
                // Email stays disabled with the tooltip — Download still works.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [url, alternates]);

    const download = async (
        targetUrl: string,
        targetFilename: string,
        key: string,
    ) => {
        setBusyKey(key);
        setError(null);
        try {
            await downloadArtifact(targetUrl, targetFilename);
        } catch (err) {
            setError(extractApiErrorMessage(err));
        } finally {
            setBusyKey(null);
        }
    };

    const onEmail = () => {
        if (!shareLinks) {
            setError('Preparing share links — try again in a moment.');
            return;
        }
        setError(null);
        const { subject, body } = composeEmail({
            filename,
            csvUrl: shareLinks.csvUrl,
            csvExpiresAt: shareLinks.csvExpiresAt,
            xlsxFilename: shareLinks.xlsxFilename,
            xlsxUrl: shareLinks.xlsxUrl,
        });
        const result = openGmailCompose({ subject, body });
        if (!result.ok && result.reason) setError(result.reason);
    };

    const xlsxAlt = alternates?.find((a) => a.format === 'xlsx');
    const primaryLabel = (format ?? 'csv').toUpperCase();

    return (
        <div className={styles.actionBar}>
            <ActionLink
                icon={<DownloadSimple size={14} weight="bold" />}
                label={`Download ${primaryLabel}`}
                busy={busyKey === url}
                onClick={() => download(url, filename, url)}
            />
            {xlsxAlt && (
                <>
                    <span className={styles.actionSeparator}>·</span>
                    <ActionLink
                        label="XLSX"
                        busy={busyKey === xlsxAlt.url}
                        onClick={() =>
                            download(xlsxAlt.url, xlsxAlt.filename, xlsxAlt.url)
                        }
                    />
                </>
            )}
            <span className={styles.actionSeparator}>·</span>
            <ActionLink
                icon={<EnvelopeSimple size={14} weight="bold" />}
                label="Email links"
                disabled={!shareLinks}
                title={
                    shareLinks
                        ? 'Open Gmail compose with download links to this report (the envelope on the table emails the table itself, and can schedule it)'
                        : 'Preparing share links…'
                }
                onClick={onEmail}
            />
            {error && <span className={styles.actionError}>{error}</span>}
        </div>
    );
}

function ActionLink({
    icon,
    label,
    busy,
    disabled,
    title,
    onClick,
}: {
    icon?: React.ReactNode;
    label: string;
    busy?: boolean;
    disabled?: boolean;
    title?: string;
    onClick: () => void;
}) {
    const className = `${styles.actionLink} ${
        disabled || busy ? styles.actionLinkDisabled : ''
    }`;
    return (
        <button
            type="button"
            className={className}
            onClick={onClick}
            disabled={disabled || busy}
            title={title}
        >
            {icon}
            <span>{busy ? `${label}…` : label}</span>
        </button>
    );
}

/**
 * Opens Gmail's compose UI in a new tab with subject + body pre-filled.
 * See the slice 2 commit history for the iteration log on why this
 * sidesteps mailto: on Chrome 124+.
 */
function openGmailCompose(args: {
    subject: string;
    body: string;
}): { ok: boolean; reason?: string } {
    const composeUrl =
        `https://mail.google.com/mail/?view=cm&fs=1` +
        `&su=${encodeURIComponent(args.subject)}` +
        `&body=${encodeURIComponent(args.body)}`;
    const opened = window.open(composeUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
        return {
            ok: false,
            reason: 'Your browser blocked the popup. Allow popups for this site and try again.',
        };
    }
    return { ok: true };
}

function composeEmail(args: {
    filename: string;
    csvUrl: string;
    csvExpiresAt: string;
    xlsxFilename?: string;
    xlsxUrl?: string | null;
}): { subject: string; body: string } {
    const reportName = humanizeFilename(args.filename);
    const subject = `Reparo report: ${reportName}`;
    const expiry = new Date(args.csvExpiresAt);
    const expiryText = Number.isNaN(expiry.getTime())
        ? 'in 7 days'
        : expiry.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
          });

    const lines = [
        `Hi,`,
        ``,
        `Sharing the "${reportName}" report from Reparo.`,
        ``,
        `Download (CSV):`,
        args.csvUrl,
    ];
    if (args.xlsxUrl && args.xlsxFilename) {
        lines.push(``, `Download (Excel):`, args.xlsxUrl);
    }
    lines.push(``, `Links expire on ${expiryText}.`, ``, `Sent from Reparo`);

    return { subject, body: lines.join('\n') };
}

function humanizeFilename(filename: string): string {
    const noExt = filename.replace(/\.(csv|xlsx)$/i, '');
    const noHash = noExt.replace(/-[a-f0-9]{8}$/i, '');
    if (!noHash) return filename;
    return noHash
        .split(/[-_]/)
        .filter(Boolean)
        .map((part) => part[0].toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
}
